/** Reine Helfer fuer den Start-Bereich (ohne React und ohne api), damit sie testbar bleiben. */
import type { AuftragSchreibStatus, HistorieStatus, LieferantErgebnis, MailStatus, SchreibErgebnis, SchreibVorschau, VersandInfo, Vorschau } from './typen';

export interface BestaetigungsZeile {
  id: string;
  name: string;
  /** Echte Empfaenger, im Testmodus mit „→ Test: <Testadresse>". */
  an: string;
  weg: 'freescout' | 'mailer' | undefined;
  positionen: number;
  auftraege: number;
  mitMail: boolean;
}

export function wegText(weg: 'freescout' | 'mailer' | undefined | null): string {
  if (weg === 'freescout') return 'FreeScout (info@)';
  if (weg === 'mailer') return 'Libero-Mailer';
  return '—';
}

/** Echte Empfaenger; im Testmodus zusaetzlich die Testadresse, an die die Mail wirklich geht. */
export function empfaengerAnzeige(empfaenger: string[], versand?: VersandInfo | null): string {
  const echt = empfaenger.length > 0 ? empfaenger.join(', ') : '—';
  if (versand?.testmodus && versand.testEmpfaenger) return `${echt} → Test: ${versand.testEmpfaenger}`;
  return echt;
}

/** Hervorhebung für Testläufe: gelbe Schrift auf Schwarz (nie weiß auf gelb). */
export const TESTLAUF_KLASSE = 'bg-[#1A1A1A] text-[#E9FF57] font-semibold';

/** Kernsatz eines Testlaufs (gleich wie im Backend, versandSperre.testlaufHinweis). */
export function testlaufText(testEmpfaenger: string | null | undefined): string {
  return `TESTLAUF: Mails nur an ${testEmpfaenger ?? '?'}, Plenty-Write nur für Aufträge mit @anstoss24.de-Kunden-Mail`;
}

/** Hinweiskasten ueber Vorschau und Dialog; null = Prod, Versand frei. Gesperrt hat Vorrang. */
export function versandHinweis(versand?: VersandInfo | null): Anzeige | null {
  if (!versand) return null;
  if (versand.gesperrt) return { text: `Versand gesperrt: ${versand.gesperrt}`, klasse: 'bg-red-50 text-red-800' };
  if (versand.testlauf) {
    const basis = testlaufText(versand.testEmpfaenger);
    // Das Backend stellt denselben Satz an den Anfang des Hinweises: nur den Rest anhängen.
    const rest = (versand.hinweis ?? '').startsWith(basis) ? (versand.hinweis ?? '').slice(basis.length).replace(/^\.\s*/, '') : versand.hinweis;
    return { text: rest ? `${basis}. ${rest}` : basis, klasse: TESTLAUF_KLASSE };
  }
  if (!versand.testmodus) return null;
  const basis = `Testmodus: alle Mails gehen an ${versand.testEmpfaenger ?? '?'} (Betreff mit [TEST → …]), nichts wird nach Plenty geschrieben`;
  return { text: versand.hinweis ? `${basis}. ${versand.hinweis}` : basis, klasse: 'bg-yellow-50 text-yellow-900' };
}

/** FreeScout-Konversation eines Lieferanten-Ergebnisses; „—" bei anderem Weg oder Altlauf. */
export function konversationText(
  l: Pick<LieferantErgebnis, 'weg' | 'mailStatus' | 'conversationId' | 'konversationNummer'>,
): string {
  if (l.weg !== 'freescout') return '—';
  if (l.conversationId == null) return l.mailStatus === 'gesendet' ? 'FreeScout: keine Konversations-ID' : '—';
  return l.konversationNummer != null
    ? `FreeScout #${l.konversationNummer} (Konversation ${l.conversationId})`
    : `FreeScout Konversation ${l.conversationId}`;
}

/**
 * Empfänger eines Lieferanten-Ergebnisses (WR-05): im Testmodus „echt → Test: <Testadresse>", sonst der echte
 * Empfänger. Altläufe ohne `gesendetAn` zeigen nur `an`. Eine Funktion für Lauf-Ergebnis und Historie.
 */
export function empfaengerErgebnisText(
  l: Pick<LieferantErgebnis, 'an' | 'gesendetAn'>,
  testmodus: boolean | undefined,
): string {
  const echt = l.an ?? '—';
  if (testmodus && l.gesendetAn && l.gesendetAn !== l.an) return `${echt} → Test: ${l.gesendetAn}`;
  if (l.gesendetAn && l.gesendetAn !== l.an) return `${echt} (gesendet an ${l.gesendetAn})`;
  return echt;
}

/** Satz zur Umleitung eines Laufs; Altlaeufe (ohne testmodus-Feld) behalten den alten Wortlaut. */
export function laufUmleitungText(
  e: { umleitung?: string | null; testmodus?: boolean; testlauf?: boolean } | null | undefined,
): string | null {
  if (!e?.umleitung) return null;
  if (e.testlauf) return `Testlauf: alle Mails gingen an ${e.umleitung}; Plenty-Write nur für Aufträge mit @anstoss24.de-Kunden-Mail`;
  if (e.testmodus) return `Testmodus: alle Mails gingen an ${e.umleitung}, nichts nach Plenty geschrieben`;
  return `Dev-Umleitung aktiv: alle Mails gingen an ${e.umleitung}`;
}

/** Zeilen fuer den Bestaetigungsdialog in Versandreihenfolge; IDs ohne Block (keine Positionen) fehlen bewusst. */
export function bestaetigungsZeilen(vorschau: Vorschau): BestaetigungsZeile[] {
  const zeilen: BestaetigungsZeile[] = [];
  for (const id of vorschau.versandReihenfolge ?? []) {
    const block = vorschau.lieferanten.find((b) => b.id === id);
    if (!block) continue;
    zeilen.push({
      id: block.id,
      name: block.name,
      an: empfaengerAnzeige(block.empfaenger, vorschau.versand),
      weg: block.weg,
      positionen: block.positionen.length,
      auftraege: block.auftragsIds.length,
      mitMail: block.positionen.length > 0,
    });
  }
  return zeilen;
}

/** Der Core blockt einen zweiten Start mit 409 JOB_ALREADY_RUNNING. */
export function istDoppelstart(err: unknown): boolean {
  const antwort = (err as any)?.response;
  return antwort?.status === 409 && antwort?.data?.error?.code === 'JOB_ALREADY_RUNNING';
}

export interface Anzeige {
  text: string;
  klasse: string;
}

export function mailStatusAnzeige(status: MailStatus): Anzeige {
  switch (status) {
    case 'gesendet':
      return { text: 'gesendet', klasse: 'bg-green-50 text-green-800' };
    case 'fehlgeschlagen':
      return { text: 'fehlgeschlagen', klasse: 'bg-red-50 text-red-800' };
    case 'unklar':
      return { text: 'Versand unklar — manuell prüfen', klasse: 'bg-orange-50 text-orange-900' };
    case 'wird_gesendet':
      return { text: 'Versand begonnen, nicht abgeschlossen — manuell prüfen', klasse: 'bg-orange-50 text-orange-900' };
    default:
      return { text: 'keine Mail', klasse: 'bg-[#DAD9D9] text-[#1A1A1A]' };
  }
}

export function laufStatusAnzeige(status: HistorieStatus): Anzeige {
  switch (status) {
    case 'laeuft':
      return { text: 'läuft', klasse: 'bg-[#DAD9D9] text-[#1A1A1A]' };
    case 'protokoll_fehler':
      return { text: 'Mails versendet, Protokoll unvollständig', klasse: 'bg-yellow-50 text-yellow-900' };
    case 'fertig':
      return { text: 'Lauf fertig', klasse: 'bg-green-50 text-green-800' };
    case 'teilweise':
      return { text: 'Lauf teilweise fehlgeschlagen', klasse: 'bg-yellow-50 text-yellow-900' };
    default:
      return { text: 'Lauf fehlgeschlagen', klasse: 'bg-red-50 text-red-800' };
  }
}

export interface SchreibVorschauAnzeige {
  zeile: string;
  /** true = es wird nichts nach Plenty geschrieben (auch bei fehlendem Schreibplan: fail closed). */
  gesperrt: boolean;
  hinweise: string[];
}

/** Dry-Run-Zeile und Hinweise; die Backend-Texte (sperrGrund, hinweise) bleiben unveraendert. */
export function schreibVorschauText(sv: SchreibVorschau | undefined | null): SchreibVorschauAnzeige {
  if (!sv) return { zeile: 'Schreibplan nicht verfügbar', gesperrt: true, hinweise: [] };
  const gesperrt = !sv.schreibenErlaubt || sv.sperrGrund !== null;
  const b = sv.budget;
  const praefix = gesperrt ? 'würde schreiben (gesperrt):' : 'würde schreiben:';
  const zeile =
    `${praefix} ${b.puts} PUTs (Status ${sv.zielStatus} + Markierung M${sv.markierung}), ` +
    `höchstens ${b.tagWritesMax} Tag-Write(s) — gesamt ≤ ${b.gesamtMax} von ${b.limitProStunde}/h`;
  const hinweise: string[] = [];
  if (sv.sperrGrund) hinweise.push(sv.sperrGrund);
  for (const h of sv.hinweise ?? []) if (!hinweise.includes(h)) hinweise.push(h);
  if (b.ueberLimit && !hinweise.some((h) => h.includes('drosselt'))) {
    hinweise.push(
      `${b.gesamtMax} Writes geplant, Plenty erlaubt ${b.limitProStunde} pro Stunde: der Plenty-Client drosselt, der Lauf dauert länger`,
    );
  }
  return { zeile, gesperrt, hinweise };
}

export interface AuftragsZeile {
  orderId: number;
  text: string;
}

/** Auftrag, Soll-Tags und Lieferanten; nur-schreiben-Auftraege mit Zusatz. */
export function schreibAuftragsZeilen(sv: SchreibVorschau | undefined | null): AuftragsZeile[] {
  if (!sv) return [];
  return sv.auftraege.map((a) => ({
    orderId: a.orderId,
    text:
      `Auftrag ${a.orderId}: Tags ${a.tagIds.length > 0 ? a.tagIds.join(', ') : '—'}` +
      (a.lieferanten.length > 0 ? ` (${a.lieferanten.join(', ')})` : '') +
      (a.nurSchreiben ? ' (nur Status/Tags, keine Mail)' : ''),
  }));
}

/** Eine Zeile je bereits bestelltem (Auftrag, Lieferant)-Paar, nach Auftrag sortiert, mit Positionsliste. */
export function bereitsBestelltZeilen(
  vorschau: Pick<Vorschau, 'bereitsBestellt'>,
): Array<{ text: string; positionen: string[] }> {
  return [...(vorschau.bereitsBestellt ?? [])]
    .sort((a, b) => a.orderId - b.orderId || a.lieferantId.localeCompare(b.lieferantId))
    .map((e) => ({
      text: `Auftrag ${e.orderId}: ${e.hinweis}`,
      positionen: (e.positionsListe ?? []).map(
        (p) => `Variation ${p.variationId}: heute ${p.mengeHeute}, damals ${p.mengeDamals === null ? 'unbekannt' : p.mengeDamals}`,
      ),
    }));
}

/** „Manuell prüfen": höhere Menge als damals bestellt — nicht versendet, Auftrag bleibt in 5.2. */
export function manuellPruefenZeilen(vorschau: Pick<Vorschau, 'manuellPruefen'>): Array<{ text: string }> {
  return [...(vorschau.manuellPruefen ?? [])]
    .sort((a, b) => a.orderId - b.orderId || a.lieferantId.localeCompare(b.lieferantId) || a.variationId - b.variationId)
    .map((e) => ({ text: `Auftrag ${e.orderId}: ${e.hinweis}` }));
}

/** Lieferanten-Tags in Plenty (zur Anzeige). */
export const TAG_NAMEN: Record<number, string> = {
  313: 'adidas',
  195: 'erima',
  196: 'Uhlsport',
  194: 'Derbystar',
  197: 'JAKO',
};

export function tagText(ids: number[]): string {
  return ids.length > 0 ? ids.map((id) => (TAG_NAMEN[id] ? `${TAG_NAMEN[id]} (${id})` : String(id))).join(', ') : '—';
}

/** IN-08: Zielstatus kommt aus dem Schreibergebnis (der 5.21-Lauf hat einen anderen). */
export function schreibStatusAnzeige(status: AuftragSchreibStatus, zielStatus: number = 5.3): Anzeige {
  switch (status) {
    case 'geschrieben':
      return { text: `auf ${zielStatus} gesetzt`, klasse: 'bg-green-50 text-green-800' };
    case 'unklar':
      return { text: 'Status gesetzt, Markierung unklar', klasse: 'bg-yellow-50 text-yellow-900' };
    case 'uebersprungen':
      return { text: 'übersprungen', klasse: 'bg-[#DAD9D9] text-[#1A1A1A]' };
    case 'fehlgeschlagen':
      return { text: 'fehlgeschlagen', klasse: 'bg-red-50 text-red-800' };
    default:
      return { text: 'nicht geschrieben', klasse: 'bg-yellow-50 text-yellow-900' };
  }
}

export interface SchreibZusammenfassung {
  zeile: string;
  /** true = es wurde (bewusst oder wegen Gate) nichts nach Plenty geschrieben. */
  gesperrt: boolean;
  warnungen: string[];
}

/** Gültiger Handlungshinweis nach einem Write-Fehler (gleicher Text wie im Backend, `HINWEIS_MAIL_RAUS`). */
export const HINWEIS_MAIL_RAUS =
  'Mail ist raus — der nächste Lauf schreibt den Auftrag nur noch (keine neue Mail), oder manuell auf 5.3 setzen.';

/**
 * Warnungen nach Write-Fehlern (WR-05). `fehlgeschlagen` heißt nach der Nachprüfung: Status steht NICHT auf 5.3.
 * Ein gesetzter Status mit abweichender Markierung ist `unklar` und bekommt einen eigenen Text.
 */
export function warnungNachWriteFehler(schreiben: SchreibErgebnis | null | undefined): string[] {
  if (!schreiben || schreiben.modus === 'gesperrt') return [];
  const w: string[] = [];
  const fehl = schreiben.auftraege.filter((a) => a.status === 'fehlgeschlagen');
  if (fehl.length > 0) {
    w.push(`Status nicht gesetzt bei Auftrag ${fehl.map((a) => a.orderId).join(', ')}: ${HINWEIS_MAIL_RAUS}`);
  }
  const unklar = schreiben.auftraege.filter((a) => a.status === 'unklar');
  if (unklar.length > 0) {
    w.push(`Status 5.3 gesetzt, Markierung prüfen bei Auftrag ${unklar.map((a) => a.orderId).join(', ')} (unklar — manuell prüfen).`);
  }
  const tagFehl = schreiben.auftraege.filter((a) => a.tagsFehler);
  if (tagFehl.length > 0) {
    w.push(`Tag manuell setzen bei Auftrag ${tagFehl.map((a) => a.orderId).join(', ')}.`);
  }
  if (schreiben.modus === 'abgebrochen' && schreiben.fehler && fehl.length === 0) {
    w.push(`Schreiben abgebrochen: ${schreiben.fehler}`);
  }
  if (schreiben.schreibStopp) {
    w.push(
      `SCHREIBSTOPP bei Auftrag ${schreiben.schreibStopp.auftrag}: ${schreiben.schreibStopp.grund}. Kein Lauf schreibt mehr nach Plenty, ` +
        `bis SCHREIB_STOPP_QUITTIERT_BIS in config.ts quittiert ist. Die Eigenschaften vor dem PUT stehen im Protokoll (unten je Auftrag).`,
    );
  }
  return w;
}

/** Anzahl der tatsächlich versendeten Lieferanten-Mails eines Laufs. */
export function mailsGesendet(ergebnis: { lieferanten?: Array<{ mailStatus: MailStatus }> } | null | undefined): number {
  return (ergebnis?.lieferanten ?? []).filter((l) => l.mailStatus === 'gesendet').length;
}

/**
 * Zusammenfassung des Plenty-Teils (WR-06): `undefined` = das Feld fehlt (Lauf vor Phase 4); `null` = abgebrochen
 * oder abgestürzt vor dem Schreiben — mit versendeten Mails als Warnung, weil die Aufträge noch im Quellstatus stehen.
 */
export function schreibZusammenfassung(
  schreiben: SchreibErgebnis | null | undefined,
  gesendeteMails = 0,
  quellStatus = 5.2,
): SchreibZusammenfassung {
  if (schreiben === undefined) {
    return { zeile: 'Plenty: keine Schreibdaten (Lauf vor Phase 4)', gesperrt: false, warnungen: [] };
  }
  if (schreiben === null) {
    if (gesendeteMails > 0) {
      return {
        zeile:
          `Plenty: nicht geschrieben (Lauf abgebrochen) — versendete Aufträge stehen noch in ${quellStatus}, der nächste Lauf schreibt sie nur noch`,
        gesperrt: true,
        warnungen: [],
      };
    }
    return { zeile: 'Plenty: nicht geschrieben (Lauf abgebrochen)', gesperrt: false, warnungen: [] };
  }
  if (schreiben.modus === 'gesperrt') {
    const grund = schreiben.auftraege.find((a) => a.grund)?.grund ?? schreiben.fehler ?? 'Schreiben gesperrt';
    return {
      // Der Grund nennt den Status oft schon selbst („… Aufträge bleiben in 3.4“) — dann nicht doppelt.
      zeile: /bleiben in/.test(grund)
        ? `Plenty: nichts geschrieben — ${grund}.`
        : `Plenty: nichts geschrieben — ${grund}. Aufträge bleiben in ${quellStatus}.`,
      gesperrt: true,
      warnungen: [],
    };
  }
  if (schreiben.modus === 'nichts_zu_schreiben') {
    return { zeile: 'Plenty: nichts zu schreiben', gesperrt: false, warnungen: [] };
  }
  const z = schreiben.zaehler;
  const zeile =
    `Plenty: ${z.geschrieben} auf ${schreiben.zielStatus} (M${schreiben.markierung}), ` +
    ((z.unklar ?? 0) > 0 ? `${z.unklar} unklar, ` : '') +
    `${z.fehlgeschlagen} fehlgeschlagen, ${z.uebersprungen} übersprungen — ` +
    `${schreiben.writes.puts} PUTs, ${schreiben.writes.tagWrites} Tag-Write(s)` +
    (schreiben.tagVariante === 'array' ? ', Tags als Array' : schreiben.tagVariante === 'einzeln' ? ', Tags einzeln' : '');
  return { zeile, gesperrt: false, warnungen: warnungNachWriteFehler(schreiben) };
}

/**
 * Testlauf: welche Aufträge würden nach Plenty geschrieben (nur Testaufträge)? null = kein Testlauf.
 * Gesperrt (Gate zu, Schreibstopp …) = keine Aufträge, mit Grund. Pure.
 */
export function testlaufSchreibListe(
  vorschau: Pick<Vorschau, 'versand' | 'schreibVorschau' | 'testauftraege'> | null | undefined,
): { titel: string; auftraege: number[]; grund: string | null } | null {
  if (!vorschau?.versand?.testlauf) return null;
  const sv = vorschau.schreibVorschau;
  const geplant = (sv?.auftraege ?? []).map((a) => a.orderId).sort((a, b) => a - b);
  if (!sv || !sv.schreibenErlaubt || sv.sperrGrund !== null) {
    return {
      titel: 'Plenty-Write im Testlauf: keiner',
      auftraege: [],
      grund: sv?.sperrGrund ?? 'Schreibplan nicht verfügbar',
    };
  }
  return {
    titel: geplant.length > 0 ? `Plenty-Write im Testlauf: ${geplant.length} Testauftrag/-aufträge` : 'Plenty-Write im Testlauf: keiner (kein Testauftrag)',
    auftraege: geplant,
    grund: null,
  };
}

/**
 * Testlauf (WR-01): Aufträge, die der Startdialog als bestätigt anzeigt und die der Start als `bestaetigteAuftragsIds`
 * mitschickt. Nur diese darf der Testlauf bestellen und schreiben. Leer = Start gesperrt (das Backend antwortet sonst 400).
 * null = kein Testlauf.
 */
export function testlaufBestaetigung(
  vorschau: Pick<Vorschau, 'versand' | 'testauftraege'> | null | undefined,
): { auftraege: number[]; startErlaubt: boolean; text: string } | null {
  if (!vorschau?.versand?.testlauf) return null;
  const auftraege = [...new Set(vorschau.testauftraege ?? [])].sort((a, b) => a - b);
  return {
    auftraege,
    startErlaubt: auftraege.length > 0,
    text:
      auftraege.length > 0
        ? `Bestätigte Testaufträge: Auftrag ${auftraege.join(', ')} — nur diese werden bestellt und geschrieben, später dazugekommene ignoriert der Lauf`
        : 'Kein Testauftrag in der Vorschau — Testlauf kann nicht gestartet werden',
  };
}

/**
 * Body für POST /starten, immer mit den bestätigten Aufträgen. Testlauf (WR-01): die angezeigten Testaufträge.
 * Echter Lauf (Maxi 02.11.2026): alle Aufträge der angezeigten Vorschau; was danach in den Status kommt, bestellt erst
 * der nächste Lauf. Ältere Antwort ohne `alleAuftragsIds`: Feld fehlt, das Backend lehnt den Start ab (400).
 */
export function startDaten(
  laufId: string,
  vorschau: Pick<Vorschau, 'versand' | 'testauftraege' | 'alleAuftragsIds'> | null | undefined,
): { laufId: string; bestaetigteAuftragsIds?: number[] } {
  const bestaetigung = testlaufBestaetigung(vorschau);
  if (bestaetigung) return { laufId, bestaetigteAuftragsIds: bestaetigung.auftraege };
  return Array.isArray(vorschau?.alleAuftragsIds) ? { laufId, bestaetigteAuftragsIds: [...vorschau!.alleAuftragsIds] } : { laufId };
}

/**
 * WR-03: Eine Vorschau-Antwort gilt nur, wenn sie zum angefragten Lauf gehört UND dieser noch gewählt ist — sonst zeigte
 * die Seite Lauf A, während der Knopf Lauf B startet. Pure.
 */
export function vorschauPasst(daten: Pick<Vorschau, 'lauf'> | null | undefined, angefragt: string, gewaehlt: string): boolean {
  return !!daten?.lauf && daten.lauf.id === angefragt && gewaehlt === angefragt;
}
